import { useCallback, useState } from 'react';
import { readColumns, toggleColumn } from '@/lib/persistedColumns';

/**
 * Column visibility persisted in localStorage. `all` and `defaults` should be module-level constants.
 * Read once (lazy initializer); written in the toggle handler. Storage failures are ignored.
 */
export function usePersistedColumns(
  storageKey: string,
  all: readonly string[],
  defaults: readonly string[],
): [string[], (key: string) => void] {
  const [visible, setVisible] = useState<string[]>(() => {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(storageKey);
    } catch {
      /* storage unavailable: use defaults */
    }
    return readColumns(raw, all, defaults);
  });

  const toggle = useCallback(
    (key: string) => {
      const next = toggleColumn(visible, all, key);
      setVisible(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* storage unavailable: the choice lasts for this page view */
      }
    },
    [visible, all, storageKey],
  );

  return [visible, toggle];
}
